import classNames from "classnames";
import React, { useEffect, useMemo, useState } from "react";
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useI18n } from "context/I18nContext";
import styles from "./DataTable.module.scss";

export type SelectOption = {
  label: string;
  value: string;
};

export type TextFilterConfig = {
  type: "text";
  label?: string;
  placeholder?: string;
};

export type SelectFilterConfig = {
  type: "select";
  label?: string;
  placeholder?: string;
  options: SelectOption[];
};

export type DateFilterConfig = {
  type: "date";
  label?: string;
  fromPlaceholder?: string;
  toPlaceholder?: string;
};

export type NumberFilterConfig = {
  type: "number";
  label?: string;
  minPlaceholder?: string;
  maxPlaceholder?: string;
};

export type BooleanFilterConfig = {
  type: "boolean";
  label?: string;
  placeholder?: string;
  trueLabel?: string;
  falseLabel?: string;
};

export type ColumnFilterConfig =
  | TextFilterConfig
  | SelectFilterConfig
  | DateFilterConfig
  | NumberFilterConfig
  | BooleanFilterConfig;

export type DataTableColumn<TData> = {
  id: string;
  header: string;
  accessorKey?: keyof TData;
  accessor?: (row: TData) => unknown;
  cell?: (value: unknown, row: TData) => React.ReactNode;
  filter?: ColumnFilterConfig;
  enableGlobalFilter?: boolean;
  align?: "left" | "center" | "right";
};

export type DataTableProps<TData> = {
  columns: DataTableColumn<TData>[];
  data: TData[];
  pageSizeOptions?: number[];
  initialPageSize?: number;
  globalSearch?: boolean;
  globalSearchPlaceholder?: string;
  emptyState?: React.ReactNode;
  loading?: boolean;
  loadingText?: string;
  manualPagination?: {
    pageIndex: number;
    pageCount: number;
    pageSize: number;
    totalItems: number;
    onPageChange: (pageIndex: number) => void;
  };
  showPaginationInfo?: boolean;
  showPaginationControls?: boolean;
};

type TextFilterState = { type: "text"; value: string };
type SelectFilterState = { type: "select"; value: string };
type DateFilterState = { type: "date"; from?: string; to?: string };
type NumberFilterState = { type: "number"; min?: string; max?: string };
type BooleanFilterState = { type: "boolean"; value?: string };

type ColumnFilterState =
  | TextFilterState
  | SelectFilterState
  | DateFilterState
  | NumberFilterState
  | BooleanFilterState;

type FilterStateMap = Record<string, ColumnFilterState>;

const DEFAULT_PAGE_SIZE_OPTIONS = [10, 25, 50];

const formatCellValue = (value: unknown) => {
  if (value === null || value === undefined) {
    return "—";
  }

  if (value instanceof Date) {
    return value.toLocaleDateString();
  }

  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }

  return String(value);
};

const toBoolean = (value: unknown): boolean | undefined => {
  if (typeof value === "boolean") {
    return value;
  }

  if (typeof value === "string") {
    const normalised = value.trim().toLowerCase();
    if (normalised === "true" || normalised === "yes" || normalised === "1") {
      return true;
    }
    if (normalised === "false" || normalised === "no" || normalised === "0") {
      return false;
    }
  }

  if (typeof value === "number") {
    return value !== 0;
  }

  return undefined;
};

const parseDate = (value: unknown): Date | null => {
  if (!value) {
    return null;
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (typeof value === "string") {
    const isoLike = /^\d{4}-\d{2}-\d{2}$/.test(value)
      ? `${value}T00:00:00`
      : value;
    const date = new Date(isoLike);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  if (typeof value === "number") {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  return null;
};

const parseNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  if (typeof value === "number") {
    return Number.isNaN(value) ? null : value;
  }

  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
};

const createInitialFilters = <TData,>(
  columns: DataTableColumn<TData>[]
): FilterStateMap =>
  columns.reduce<FilterStateMap>((acc, column) => {
    if (!column.filter) {
      return acc;
    }

    switch (column.filter.type) {
      case "text":
        acc[column.id] = { type: "text", value: "" };
        break;
      case "select":
        acc[column.id] = { type: "select", value: "" };
        break;
      case "date":
        acc[column.id] = { type: "date" };
        break;
      case "number":
        acc[column.id] = { type: "number" };
        break;
      case "boolean":
        acc[column.id] = { type: "boolean" };
        break;
      default:
        break;
    }

    return acc;
  }, {});

const getColumnValue = <TData,>(
  row: TData,
  column: DataTableColumn<TData>
) => {
  if (column.accessor) {
    return column.accessor(row);
  }

  if (column.accessorKey) {
    return (row as Record<string, unknown>)[column.accessorKey as string];
  }

  return (row as Record<string, unknown>)[column.id];
};

const getAlignClass = (align?: string) => {
  if (align === "right") {
    return styles.alignRight;
  }
  if (align === "center") {
    return styles.alignCenter;
  }
  return undefined;
};

function DataTable<TData extends Record<string, unknown>>({
  columns,
  data,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  initialPageSize = DEFAULT_PAGE_SIZE_OPTIONS[0],
  globalSearch = true,
  globalSearchPlaceholder,
  emptyState,
  loading = false,
  loadingText,
  manualPagination,
  showPaginationInfo = true,
  showPaginationControls = true,
}: DataTableProps<TData>) {
  const { t } = useI18n();
  const isManualPagination = Boolean(manualPagination);
  const [filters, setFilters] = useState<FilterStateMap>(() =>
    createInitialFilters(columns)
  );
  const [globalQuery, setGlobalQuery] = useState("");
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [pageIndex, setPageIndex] = useState(0);

  useEffect(() => {
    setFilters(createInitialFilters(columns));
  }, [columns]);

  const searchableColumns = useMemo(
    () => columns.filter((col) => col.enableGlobalFilter),
    [columns]
  );
  const isGlobalSearchEnabled =
    globalSearch && searchableColumns.length > 0;

  useEffect(() => {
    if (!isGlobalSearchEnabled && globalQuery) {
      setGlobalQuery("");
    }
  }, [isGlobalSearchEnabled, globalQuery]);

  const handleFilterChange = (
    columnId: string,
    newState: Partial<ColumnFilterState>
  ) => {
    setFilters((prev) => {
      const current = prev[columnId];
      if (!current) {
        return prev;
      }

      return {
        ...prev,
        [columnId]: {
          ...current,
          ...newState,
        } as ColumnFilterState,
      };
    });
    setPageIndex(0);
  };

  const filteredData = useMemo(() => {
    if (isManualPagination) {
      return data;
    }

    const query = globalQuery.trim().toLowerCase();

    return data.filter((row) => {
      if (isGlobalSearchEnabled && query) {
        const matchesGlobal = searchableColumns.some((column) => {
          const value = getColumnValue(row, column);
          if (value === null || value === undefined) {
            return false;
          }
          return String(value).toLowerCase().includes(query);
        });

        if (!matchesGlobal) {
          return false;
        }
      }

      return columns.every((column) => {
        const filterState = filters[column.id];
        if (!filterState) {
          return true;
        }

        const value = getColumnValue(row, column);

        switch (filterState.type) {
          case "text": {
            const needle = filterState.value?.trim().toLowerCase();
            if (!needle) {
              return true;
            }
            if (value === null || value === undefined) {
              return false;
            }
            return String(value).toLowerCase().includes(needle);
          }
          case "select": {
            if (!filterState.value) {
              return true;
            }
            if (value === null || value === undefined) {
              return false;
            }
            return String(value) === filterState.value;
          }
          case "boolean": {
            if (!filterState.value) {
              return true;
            }
            const boolValue = toBoolean(value);
            if (filterState.value === "true") {
              return boolValue === true;
            }
            if (filterState.value === "false") {
              return boolValue === false;
            }
            return true;
          }
          case "date": {
            const from = filterState.from
              ? new Date(`${filterState.from}T00:00:00`)
              : null;
            const to = filterState.to
              ? new Date(`${filterState.to}T23:59:59.999`)
              : null;

            if (!from && !to) {
              return true;
            }

            const dateValue = parseDate(value);
            if (!dateValue) {
              return false;
            }

            if (from && dateValue < from) {
              return false;
            }

            if (to && dateValue > to) {
              return false;
            }

            return true;
          }
          case "number": {
            const min = parseNumber(filterState.min);
            const max = parseNumber(filterState.max);

            if (min === null && max === null) {
              return true;
            }

            const numericValue = parseNumber(value);
            if (numericValue === null) {
              return false;
            }

            if (min !== null && numericValue < min) {
              return false;
            }

            if (max !== null && numericValue > max) {
              return false;
            }

            return true;
          }
          default:
            return true;
        }
      });
    });
  }, [
    columns,
    data,
    filters,
    globalQuery,
    isGlobalSearchEnabled,
    searchableColumns,
    isManualPagination,
  ]);

  useEffect(() => {
    if (!isManualPagination) {
      setPageIndex(0);
    }
  }, [filteredData.length, isManualPagination]);

  const autoPageCount = Math.max(1, Math.ceil(filteredData.length / pageSize));
  const autoSafePageIndex = Math.min(pageIndex, autoPageCount - 1);
  const autoPageStart = autoSafePageIndex * pageSize;
  const autoPageEnd = autoPageStart + pageSize;

  const displayData = useMemo(
    () =>
      isManualPagination
        ? data
        : filteredData.slice(autoPageStart, autoPageEnd),
    [data, filteredData, isManualPagination, autoPageEnd, autoPageStart]
  );

  const columnDefs = useMemo<ColumnDef<TData>[]>(
    () =>
      columns.map((column) => ({
        id: column.id,
        header: column.header,
        accessorKey: column.accessorKey
          ? String(column.accessorKey)
          : undefined,
        accessorFn: column.accessor,
        cell: ({ getValue, row }) => {
          const rawValue =
            column.cell || column.accessor || column.accessorKey
              ? getColumnValue(row.original, column)
              : getValue();

          if (column.cell) {
            return column.cell(rawValue, row.original);
          }

          return formatCellValue(rawValue);
        },
        meta: {
          align: column.align,
        },
      })),
    [columns]
  );

  const table = useReactTable({
    data: displayData,
    columns: columnDefs,
    getCoreRowModel: getCoreRowModel(),
  });

  const currentPageIndex = isManualPagination
    ? manualPagination!.pageIndex
    : autoSafePageIndex;

  const totalPages = isManualPagination
    ? Math.max(manualPagination!.pageCount, 1)
    : autoPageCount;

  const handlePageSizeChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const nextSize = Number(event.target.value);
    setPageSize(nextSize);
    setPageIndex(0);
  };

  const goToPage = (nextIndex: number) => {
    if (isManualPagination) {
      const clamped = Math.min(Math.max(nextIndex, 0), totalPages - 1);
      manualPagination?.onPageChange(clamped);
    } else {
      setPageIndex((prev) => {
        const clamped = Math.min(Math.max(nextIndex, 0), totalPages - 1);
        return clamped;
      });
    }
  };

  const handlePreviousPage = () => {
    goToPage(currentPageIndex - 1);
  };

  const handleNextPage = () => {
    goToPage(currentPageIndex + 1);
  };

  const manualPageSize = manualPagination?.pageSize ?? pageSize;
  const manualTotalItems = manualPagination?.totalItems ?? filteredData.length;
  const startIndex = isManualPagination
    ? manualPagination!.pageIndex * manualPageSize
    : autoPageStart;
  const endIndex = isManualPagination
    ? startIndex + manualPageSize
    : autoPageEnd;
  const showingFrom = manualTotalItems === 0 ? 0 : startIndex + 1;
  const showingTo = Math.min(endIndex, manualTotalItems);
  const paginationInfoText = t("datatable.showing", {
    from: showingFrom,
    to: showingTo,
    total: manualTotalItems,
  });
  const tableHasRows = table.getRowModel().rows.length > 0;
  const totalColumns = table.getAllLeafColumns().length || columns.length;
  const disablePrevious = currentPageIndex <= 0;
  const disableNext = currentPageIndex >= totalPages - 1;
  const searchLabel = t("datatable.search");
  const searchPlaceholder =
    globalSearchPlaceholder ?? t("datatable.searchPlaceholder");
  const rowsLabel = t("datatable.rows");
  const previousLabel = t("datatable.previous");
  const nextLabel = t("datatable.next");
  const resolvedEmptyState = emptyState ?? t("datatable.empty");
  const resolvedLoadingText = loadingText ?? t("datatable.loading");

  return (
    <div className={styles.wrapper}>
      {(isGlobalSearchEnabled || Object.keys(filters).length > 0) && (
        <div className={styles.toolbar}>
          {isGlobalSearchEnabled && (
            <div className={styles.filterGroup}>
              <label className={styles.label} htmlFor="datatable-global-search">
                {searchLabel}
              </label>
              <input
                id="datatable-global-search"
                className={styles.input}
                type="search"
                value={globalQuery}
                placeholder={searchPlaceholder}
                onChange={(event) => {
                  setGlobalQuery(event.target.value);
                  setPageIndex(0);
                }}
              />
            </div>
          )}

          {columns
            .filter((column) => column.filter)
            .map((column) => {
              const filter = column.filter!;
              const filterState = filters[column.id];
              const label = filter.label ?? column.header;

              switch (filter.type) {
                case "text":
                  return (
                    <div className={styles.filterGroup} key={column.id}>
                      <label className={styles.label} htmlFor={`${column.id}-filter`}>
                        {label}
                      </label>
                      <input
                        id={`${column.id}-filter`}
                        className={styles.input}
                        type="search"
                        placeholder={filter.placeholder ?? t("datatable.searchPlaceholder")}
                        value={(filterState as TextFilterState)?.value ?? ""}
                        onChange={(event) =>
                          handleFilterChange(column.id, {
                            value: event.target.value,
                          })
                        }
                      />
                    </div>
                  );
                case "select":
                  return (
                    <div className={styles.filterGroup} key={column.id}>
                      <label className={styles.label} htmlFor={`${column.id}-filter`}>
                        {label}
                      </label>
                      <select
                        id={`${column.id}-filter`}
                        className={styles.select}
                        value={(filterState as SelectFilterState)?.value ?? ""}
                        onChange={(event) =>
                          handleFilterChange(column.id, {
                            value: event.target.value,
                          })
                        }
                      >
                        <option value="">{filter.placeholder ?? t("datatable.all")}</option>
                        {filter.options.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  );
                case "date":
                  return (
                    <div className={styles.filterGroup} key={column.id}>
                      <span className={styles.label}>{label}</span>
                      <div className={styles.rangeInputs}>
                        <input
                          className={styles.input}
                          type="date"
                          value={(filterState as DateFilterState)?.from ?? ""}
                          placeholder={filter.fromPlaceholder ?? t("datatable.from")}
                          onChange={(event) =>
                            handleFilterChange(column.id, {
                              from: event.target.value,
                            })
                          }
                        />
                        <input
                          className={styles.input}
                          type="date"
                          value={(filterState as DateFilterState)?.to ?? ""}
                          placeholder={filter.toPlaceholder ?? t("datatable.to")}
                          onChange={(event) =>
                            handleFilterChange(column.id, {
                              to: event.target.value,
                            })
                          }
                        />
                      </div>
                    </div>
                  );
                case "number":
                  return (
                    <div className={styles.filterGroup} key={column.id}>
                      <span className={styles.label}>{label}</span>
                      <div className={styles.rangeInputs}>
                        <input
                          className={styles.input}
                          type="number"
                          value={(filterState as NumberFilterState)?.min ?? ""}
                          placeholder={filter.minPlaceholder ?? t("datatable.min")}
                          onChange={(event) =>
                            handleFilterChange(column.id, {
                              min: event.target.value,
                            })
                          }
                        />
                        <input
                          className={styles.input}
                          type="number"
                          value={(filterState as NumberFilterState)?.max ?? ""}
                          placeholder={filter.maxPlaceholder ?? t("datatable.max")}
                          onChange={(event) =>
                            handleFilterChange(column.id, {
                              max: event.target.value,
                            })
                          }
                        />
                      </div>
                    </div>
                  );
                case "boolean":
                  const allLabel = filter.placeholder ?? t("datatable.all");
                  const trueLabel = filter.trueLabel ?? t("datatable.booleanTrue");
                  const falseLabel = filter.falseLabel ?? t("datatable.booleanFalse");
                  return (
                    <div className={styles.filterGroup} key={column.id}>
                      <label className={styles.label} htmlFor={`${column.id}-filter`}>
                        {label}
                      </label>
                      <select
                        id={`${column.id}-filter`}
                        className={styles.select}
                        value={(filterState as BooleanFilterState)?.value ?? ""}
                        onChange={(event) =>
                          handleFilterChange(column.id, {
                            value: event.target.value,
                          })
                        }
                      >
                        <option value="">{allLabel}</option>
                        <option value="true">{trueLabel}</option>
                        <option value="false">{falseLabel}</option>
                      </select>
                    </div>
                  );
                default:
                  return null;
              }
            })}
        </div>
      )}

      <div className={styles.tableWrap}>
        <div className={styles.tableViewport}>
          <table className={styles.table}>
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      className={classNames(
                        getAlignClass(
                          (header.column.columnDef.meta as { align?: string })?.align
                        )
                      )}
                      scope="col"
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {tableHasRows ? (
                table.getRowModel().rows.map((row) => (
                  <tr key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <td
                        key={cell.id}
                        className={classNames(
                          getAlignClass(
                            (cell.column.columnDef.meta as { align?: string })?.align
                          )
                        )}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))
              ) : (
                <tr className={styles.emptyRow}>
                  <td colSpan={totalColumns}>{resolvedEmptyState}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {loading && (
          <div className={styles.loadingOverlay}>{resolvedLoadingText}</div>
        )}
      </div>

      <div className={styles.pagination}>
        {showPaginationInfo && (
          <div className={styles.paginationInfo}>{paginationInfoText}</div>
        )}

        {showPaginationControls && (
          <div className={styles.paginationControls}>
            {!isManualPagination && (
              <>
                <label className={styles.label} htmlFor="datatable-page-size">
                  {rowsLabel}
                </label>
                <select
                  id="datatable-page-size"
                  className={styles.pageSizeSelect}
                  value={pageSize}
                  onChange={handlePageSizeChange}
                >
                  {pageSizeOptions.map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
              </>
            )}

            <button
              type="button"
              className={styles.button}
              onClick={handlePreviousPage}
              disabled={disablePrevious}
            >
              {previousLabel}
            </button>
            <button
              type="button"
              className={styles.button}
              onClick={handleNextPage}
              disabled={disableNext}
            >
              {nextLabel}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default DataTable;
